import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  HelpCircle,
  Info,
  X,
  Sparkles,
  Trash2,
  ShieldAlert,
} from "lucide-react";

export type DialogVariant = "danger" | "warning" | "info" | "success" | "neutral";

export interface ConfirmOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: DialogVariant;
  icon?: "trash" | "warning" | "info" | "shield" | "sparkles" | "check";
  details?: string;
}

export interface AlertOptions {
  title?: string;
  message: string;
  okText?: string;
  variant?: DialogVariant;
  icon?: "info" | "warning" | "danger" | "success" | "shield";
  details?: string;
}

export interface PromptOptions {
  title?: string;
  message: string;
  placeholder?: string;
  defaultValue?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: DialogVariant;
}

interface DialogState {
  isOpen: boolean;
  type: "confirm" | "alert" | "prompt";
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant: DialogVariant;
  icon?: string;
  details?: string;
  promptValue?: string;
  promptPlaceholder?: string;
  resolve?: (value: any) => void;
}

interface AppDialogContextValue {
  confirm: (options: ConfirmOptions | string) => Promise<boolean>;
  alert: (options: AlertOptions | string) => Promise<void>;
  prompt: (options: PromptOptions | string) => Promise<string | null>;
}

const AppDialogContext = createContext<AppDialogContextValue | null>(null);

// Global event bus fallback so utilities outside React components can trigger dialogs
type DialogListener = (state: DialogState) => void;
let globalDialogListener: DialogListener | null = null;

export const appConfirm = (options: ConfirmOptions | string): Promise<boolean> => {
  const opts: ConfirmOptions = typeof options === "string" ? { message: options } : options;
  return new Promise<boolean>((resolve) => {
    if (globalDialogListener) {
      globalDialogListener({
        isOpen: true,
        type: "confirm",
        title: opts.title || "Confirmation Required",
        message: opts.message,
        confirmText: opts.confirmText || "Confirm",
        cancelText: opts.cancelText || "Cancel",
        variant: opts.variant || "warning",
        icon: opts.icon,
        details: opts.details,
        resolve,
      });
    } else {
      // Fallback
      resolve(true);
    }
  });
};

export const appAlert = (options: AlertOptions | string): Promise<void> => {
  const opts: AlertOptions = typeof options === "string" ? { message: options } : options;
  return new Promise<void>((resolve) => {
    if (globalDialogListener) {
      globalDialogListener({
        isOpen: true,
        type: "alert",
        title: opts.title || "Notification",
        message: opts.message,
        confirmText: opts.okText || "OK",
        variant: opts.variant || (opts.message.toLowerCase().includes("error") || opts.message.toLowerCase().includes("failed") ? "danger" : "info"),
        icon: opts.icon,
        details: opts.details,
        resolve: () => resolve(),
      });
    } else {
      resolve();
    }
  });
};

export const appPrompt = (options: PromptOptions | string): Promise<string | null> => {
  const opts: PromptOptions = typeof options === "string" ? { message: options } : options;
  return new Promise<string | null>((resolve) => {
    if (globalDialogListener) {
      globalDialogListener({
        isOpen: true,
        type: "prompt",
        title: opts.title || "Input Required",
        message: opts.message,
        confirmText: opts.confirmText || "Submit",
        cancelText: opts.cancelText || "Cancel",
        variant: opts.variant || "neutral",
        promptPlaceholder: opts.placeholder || "",
        promptValue: opts.defaultValue || "",
        resolve,
      });
    } else {
      resolve(null);
    }
  });
};

export function useAppDialog(): AppDialogContextValue {
  const ctx = useContext(AppDialogContext);
  if (!ctx) {
    return {
      confirm: appConfirm,
      alert: appAlert,
      prompt: appPrompt,
    };
  }
  return ctx;
}

export function AppDialogProvider({ children }: { children: React.ReactNode }) {
  const [dialog, setDialog] = useState<DialogState>({
    isOpen: false,
    type: "confirm",
    title: "",
    message: "",
    variant: "neutral",
  });
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    globalDialogListener = (state) => {
      setDialog(state);
      setInputValue(state.promptValue || "");
    };
    return () => {
      globalDialogListener = null;
    };
  }, []);

  const confirm = useCallback((options: ConfirmOptions | string): Promise<boolean> => {
    return appConfirm(options);
  }, []);

  const alert = useCallback((options: AlertOptions | string): Promise<void> => {
    return appAlert(options);
  }, []);

  const prompt = useCallback((options: PromptOptions | string): Promise<string | null> => {
    return appPrompt(options);
  }, []);

  const handleClose = (result: any) => {
    if (dialog.resolve) {
      dialog.resolve(result);
    }
    setDialog((prev) => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    if (dialog.isOpen && dialog.type === "prompt") {
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 100);
    }
  }, [dialog.isOpen, dialog.type]);

  // Keyboard shortcut listener
  useEffect(() => {
    if (!dialog.isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        handleClose(dialog.type === "confirm" ? false : null);
      } else if (e.key === "Enter" && !e.shiftKey) {
        if (dialog.type === "prompt") {
          e.preventDefault();
          handleClose(inputValue);
        } else if (dialog.type === "confirm") {
          e.preventDefault();
          handleClose(true);
        } else if (dialog.type === "alert") {
          e.preventDefault();
          handleClose(undefined);
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [dialog.isOpen, dialog.type, inputValue]);

  const renderIcon = () => {
    if (dialog.icon === "trash") return <Trash2 className="w-5 h-5 text-rose-400" />;
    if (dialog.icon === "sparkles") return <Sparkles className="w-5 h-5 text-amber-400" />;
    if (dialog.icon === "shield") return <ShieldAlert className="w-5 h-5 text-rose-400" />;

    switch (dialog.variant) {
      case "danger":
        return <AlertCircle className="w-5 h-5 text-rose-400" />;
      case "warning":
        return <AlertTriangle className="w-5 h-5 text-amber-400" />;
      case "success":
        return <CheckCircle2 className="w-5 h-5 text-emerald-400" />;
      case "info":
        return <Info className="w-5 h-5 text-blue-400" />;
      default:
        return <HelpCircle className="w-5 h-5 text-zinc-400" />;
    }
  };

  const getHeaderBadgeClass = () => {
    switch (dialog.variant) {
      case "danger":
        return "bg-rose-950/60 border-rose-800/60 text-rose-300";
      case "warning":
        return "bg-amber-950/60 border-amber-800/60 text-amber-300";
      case "success":
        return "bg-emerald-950/60 border-emerald-800/60 text-emerald-300";
      case "info":
        return "bg-blue-950/60 border-blue-800/60 text-blue-300";
      default:
        return "bg-zinc-900 border-zinc-800 text-zinc-300";
    }
  };

  const getConfirmButtonClass = () => {
    switch (dialog.variant) {
      case "danger":
        return "bg-rose-600 hover:bg-rose-500 text-white shadow-lg shadow-rose-950/50 border border-rose-500 hover:border-rose-300";
      case "warning":
        return "bg-amber-600 hover:bg-amber-500 text-white shadow-lg shadow-amber-950/50 border border-amber-500 hover:border-amber-300";
      case "success":
        return "bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-950/50 border border-emerald-500 hover:border-emerald-300";
      case "info":
        return "bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-950/50 border border-blue-500 hover:border-blue-300";
      default:
        return "bg-amber-500 hover:bg-amber-400 text-black font-bold shadow-lg shadow-amber-950/40 border border-amber-400 hover:border-amber-200";
    }
  };

  return (
    <AppDialogContext.Provider value={{ confirm, alert, prompt }}>
      {children}

      <AnimatePresence>
        {dialog.isOpen && (
          <div
            id="app-dialog-overlay"
            className="fixed inset-0 z-[99999] flex items-center justify-center p-4 sm:p-6 overflow-y-auto bg-black/80 backdrop-blur-md transition-opacity"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                handleClose(dialog.type === "confirm" ? false : null);
              }
            }}
          >
            <motion.div
              id="app-dialog-card"
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
              className="relative w-full max-w-lg bg-zinc-950 border border-zinc-800 rounded-2xl shadow-2xl overflow-hidden text-slate-100 flex flex-col"
              role="dialog"
              aria-modal="true"
              aria-labelledby="app-dialog-title"
            >
              {/* Header Accent Bar */}
              <div
                className={`h-1.5 w-full ${
                  dialog.variant === "danger"
                    ? "bg-gradient-to-r from-rose-600 via-rose-500 to-amber-600"
                    : dialog.variant === "warning"
                    ? "bg-gradient-to-r from-amber-600 via-amber-500 to-yellow-500"
                    : dialog.variant === "success"
                    ? "bg-gradient-to-r from-emerald-600 via-teal-500 to-emerald-400"
                    : dialog.variant === "info"
                    ? "bg-gradient-to-r from-blue-600 via-indigo-500 to-cyan-400"
                    : "bg-gradient-to-r from-amber-500 via-zinc-600 to-amber-600"
                }`}
              />

              <div className="p-5 sm:p-6 space-y-4">
                {/* Title & Icon Header */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-xl border flex items-center justify-center shrink-0 ${getHeaderBadgeClass()}`}
                    >
                      {renderIcon()}
                    </div>
                    <div>
                      <h3
                        id="app-dialog-title"
                        className="font-display font-bold text-base sm:text-lg text-slate-100 leading-tight"
                      >
                        {dialog.title || "Notification"}
                      </h3>
                      <span className="text-[11px] font-mono text-zinc-500 uppercase tracking-wider">
                        KellyKar System
                      </span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleClose(dialog.type === "confirm" ? false : null)}
                    className="text-zinc-500 hover:text-zinc-200 p-1.5 rounded-[2px] border border-transparent hover:border-zinc-700 hover:bg-zinc-900 transition-all cursor-pointer active:scale-95"
                    aria-label="Close dialog"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Message Body */}
                <div className="text-sm text-zinc-300 leading-relaxed whitespace-pre-line py-1">
                  {dialog.message}
                </div>

                {/* Optional Expandable or Detailed Box */}
                {dialog.details && (
                  <div className="p-3 rounded-[2px] bg-zinc-900/70 border border-zinc-800 text-xs font-mono text-zinc-400 max-h-36 overflow-y-auto whitespace-pre-wrap">
                    {dialog.details}
                  </div>
                )}

                {/* Optional Prompt Input */}
                {dialog.type === "prompt" && (
                  <div className="pt-2">
                    <input
                      ref={inputRef}
                      type="text"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      placeholder={dialog.promptPlaceholder}
                      className="w-full px-3.5 py-2.5 bg-black border border-zinc-700 focus:border-amber-400 rounded-[2px] text-sm text-slate-100 placeholder-zinc-600 focus:outline-none transition-colors"
                    />
                  </div>
                )}
              </div>

              {/* Actions Footer */}
              <div className="px-5 py-4 bg-zinc-900/50 border-t border-zinc-900 flex items-center justify-end gap-2.5">
                {dialog.type !== "alert" && (
                  <button
                    type="button"
                    onClick={() => handleClose(dialog.type === "confirm" ? false : null)}
                    className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-zinc-300 hover:text-white bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 hover:border-zinc-500 rounded-[2px] transition-all cursor-pointer active:scale-95"
                  >
                    {dialog.cancelText || "Cancel"}
                  </button>
                )}

                <button
                  type="button"
                  autoFocus
                  onClick={() => {
                    if (dialog.type === "prompt") {
                      handleClose(inputValue);
                    } else if (dialog.type === "confirm") {
                      handleClose(true);
                    } else {
                      handleClose(undefined);
                    }
                  }}
                  className={`px-5 py-2 text-xs font-semibold uppercase tracking-wider rounded-[2px] transition-all cursor-pointer border active:scale-95 ${getConfirmButtonClass()}`}
                >
                  {dialog.confirmText || (dialog.type === "alert" ? "Got It" : "Confirm")}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </AppDialogContext.Provider>
  );
}
